import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { User, Building, Save } from "lucide-react";
import { AuthorizedSignatory } from "../../types/schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ourSignatory: AuthorizedSignatory;
  customerSignatory: AuthorizedSignatory;
  onSave: (ourSignatory: AuthorizedSignatory, customerSignatory: AuthorizedSignatory) => void;
}

const OUR_NAME_OPTIONS = [
  "Tarun Soni",
  "Shagun Jain",
  "Winston Pinto",
  "Piyush Patel",
  "Vishal Shinkar"
];

const NAME_ROLE_MAP: Record<string, string> = {
  "Tarun Soni": "Director",
  "Shagun Jain": "Director",
  "Winston Pinto": "Authorized Signatory",
  "Piyush Patel": "Authorized Signatory",
  "Vishal Shinkar": "Authorized Signatory",
};

const emptySignatory: AuthorizedSignatory = {
  name: "",
  role: "",
};

export default function EditSignatoriesModal({ 
  open, 
  onOpenChange, 
  ourSignatory, 
  customerSignatory, 
  onSave 
}: Props) {   
  const [editOurSignatory, setEditOurSignatory] = useState<AuthorizedSignatory>(ourSignatory || emptySignatory);
  const [editCustomerSignatory, setEditCustomerSignatory] = useState<AuthorizedSignatory>(customerSignatory || emptySignatory);

  useEffect(() => {
    if (ourSignatory) {
      setEditOurSignatory(ourSignatory);
    } else {
      setEditOurSignatory(emptySignatory);
    }
  }, [ourSignatory, open]);

  const handleSave = () => {
    onSave(editOurSignatory, editCustomerSignatory);
    onOpenChange(false);
  };

  const updateOurSignatory = (field: keyof AuthorizedSignatory, value: any) => {
    setEditOurSignatory(prev => ({ ...prev, [field]: value }));
  };

  const updateCustomerSignatory = (field: keyof AuthorizedSignatory, value: any) => {
    setEditCustomerSignatory(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Authorised Signatories</DialogTitle>
          <DialogDescription>
            Update signatory details and roles for this lease agreement
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Our Signatory */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Our Signatory
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="our-name">Full Name</Label>
                  <Select
                    value={editOurSignatory.name || ""}
                    onValueChange={(val) => {
                      const role = NAME_ROLE_MAP[val] ?? editOurSignatory.role ?? "";
                      setEditOurSignatory((prev) => ({
                        ...prev,
                        name: val,
                        role,
                      }));
                    }}
                  >
                    <SelectTrigger id="our-name">
                      <SelectValue placeholder="Select name" />
                    </SelectTrigger>
                    <SelectContent>
                      {OUR_NAME_OPTIONS.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="our-role">Role/Designation</Label>
                  <Input
                    disabled={true}
                    id="our-role"
                    value={NAME_ROLE_MAP[editOurSignatory.name || ""] ?? editOurSignatory.role}
                    onChange={(e) => updateOurSignatory('role', e.target.value)}
                    placeholder="e.g., Managing Director"
                  />
                </div>
              </div>
              
              {/* <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="our-status">Signature Status</Label>
                  <Select 
                    value={editOurSignatory.signatureStatus} 
                    onValueChange={(value: SignatureStatus) => updateOurSignatory('signatureStatus', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Signed">Signed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Badge variant={editOurSignatory.signatureStatus === 'Signed' ? 'success' : 'secondary'}>
                    {editOurSignatory.signatureStatus}
                  </Badge>
                </div>
              </div> */}

              {/* {editOurSignatory.signatureStatus === 'Signed' && (
                <div>
                  <Label htmlFor="our-signed-date">Signed Date</Label>
                  <Input
                    id="our-signed-date"
                    type="date"
                    value={editOurSignatory.signedDate || ''}
                    onChange={(e) => updateOurSignatory('signedDate', e.target.value)}
                  />
                </div>
              )} */}
            </CardContent>
          </Card>

          {/* Customer Signatory */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="h-5 w-5" />
                Customer Signatory
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer-name">Full Name</Label>
                  <Input
                    id="customer-name"
                    value={editCustomerSignatory.name}
                    onChange={(e) => updateCustomerSignatory('name', e.target.value)}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <Label htmlFor="customer-role">Role/Designation</Label>
                  <Input
                    id="customer-role"
                    value={editCustomerSignatory.role}
                    onChange={(e) => updateCustomerSignatory('role', e.target.value)}
                    placeholder="e.g., Authorized Signatory"
                  />
                </div>
              </div>
              
              {/* <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer-status">Signature Status</Label>
                  <Select 
                    value={editCustomerSignatory.signatureStatus} 
                    onValueChange={(value: SignatureStatus) => updateCustomerSignatory('signatureStatus', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Signed">Signed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Badge variant={editCustomerSignatory.signatureStatus === 'Signed' ? 'success' : 'secondary'}>
                    {editCustomerSignatory.signatureStatus}
                  </Badge>
                </div>
              </div> */}

              {/* {editCustomerSignatory.signatureStatus === 'Signed' && (
                <div>
                  <Label htmlFor="customer-signed-date">Signed Date</Label>
                  <Input
                    id="customer-signed-date"
                    type="date"
                    value={editCustomerSignatory.signedDate || ''}
                    onChange={(e) => updateCustomerSignatory('signedDate', e.target.value)}
                  />
                </div>
              )} */}
            </CardContent>
          </Card>

          {/* Additional Options */}
          {/* <Card className="bg-muted/30">
            <CardHeader>
              <CardTitle className="text-sm">Additional Options</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Plus className="h-3 w-3 mr-2" />
                  Add Witness
                </Button>
                <Button variant="outline" size="sm">
                  <Plus className="h-3 w-3 mr-2" />
                  Add Co-Signatory
                </Button>
              </div>
            </CardContent>
          </Card> */}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}